import { create } from 'zustand'
import {
  applyDocumentPatch,
  createBaseDocument,
  createInitialDocument,
  type CanvasNode,
  type DesignTokens,
  type DocumentPatch,
  type StudioDocument,
} from './model'
import { compileDocument, parseDocumentCode } from './serializer'

export type ChangeSource = 'design' | 'code' | 'agent' | 'history' | 'system'

export interface HistorySnapshot {
  document: StudioDocument
  label: string
  source: ChangeSource
}

export interface MutationRecord {
  id: string
  label: string
  source: ChangeSource
  at: number
  durationMs: number
}

export interface BranchRecord {
  id: string
  name: string
  parentId: string | null
  author: string
  createdAt: number
  baseCode: string
  color: string
}

export interface VersionCommit {
  id: string
  branchId: string
  message: string
  author: string
  at: number
  code: string
}

export interface ApplyCodeResult {
  ok: boolean
  error?: string
}

export interface StudioState {
  document: StudioDocument
  selectedNodeId: string
  editorDraft: string
  codeError: string | null
  history: HistorySnapshot[]
  future: HistorySnapshot[]
  mutations: MutationRecord[]
  branches: BranchRecord[]
  commits: VersionCommit[]
  activeBranchId: string
  selectNode: (nodeId: string) => void
  updateNode: (
    nodeId: string,
    patch: Partial<Pick<CanvasNode, 'text' | 'styles' | 'name'>>,
    label?: string,
  ) => void
  updateToken: (token: keyof DesignTokens, value: string) => void
  applyPatch: (
    patch: DocumentPatch,
    label: string,
    source?: ChangeSource,
  ) => void
  setEditorDraft: (source: string) => void
  applyEditorDraft: () => ApplyCodeResult
  formatEditorDraft: () => void
  undo: () => void
  redo: () => void
  reset: () => void
  createRemix: () => void
  createCheckpoint: (message: string) => void
}

const MAX_HISTORY = 40
const now = Date.now()
const baseDocument = createBaseDocument()
const initialDocument = createInitialDocument()
const baseCode = compileDocument(baseDocument)
const initialCode = compileDocument(initialDocument)

const initialBranches: BranchRecord[] = [
  {
    id: 'main',
    name: 'main',
    parentId: null,
    author: 'driftless',
    createdAt: now - 1000 * 60 * 64,
    baseCode,
    color: '#8a8f98',
  },
  {
    id: 'aurora-remix',
    name: 'aurora-remix',
    parentId: 'main',
    author: 'you',
    createdAt: now - 1000 * 60 * 18,
    baseCode,
    color: '#c6ff5c',
  },
]

const initialCommits: VersionCommit[] = [
  {
    id: 'c0f14a2',
    branchId: 'main',
    message: 'Create Northstar launch surface',
    author: 'Maya Chen',
    at: now - 1000 * 60 * 64,
    code: baseCode,
  },
  {
    id: '92bd7e1',
    branchId: 'aurora-remix',
    message: 'Remix hero direction',
    author: 'you',
    at: now - 1000 * 60 * 18,
    code: initialCode,
  },
]

function recordId(): string {
  return typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(16).slice(2)}`
}

function makeMutation(
  label: string,
  source: ChangeSource,
  durationMs: number,
): MutationRecord {
  return {
    id: recordId(),
    label,
    source,
    at: Date.now(),
    durationMs,
  }
}

function replaceTokenReferences(
  document: StudioDocument,
  tokenPatch: Partial<DesignTokens>,
): StudioDocument {
  if (Object.keys(tokenPatch).length === 0) return document

  const replacements = Object.entries(tokenPatch).map(([token, nextValue]) => ({
    previousValue: document.tokens[token as keyof DesignTokens],
    nextValue,
  }))
  const nodes = Object.fromEntries(
    Object.entries(document.nodes).map(([nodeId, node]) => {
      const styles = Object.fromEntries(
        Object.entries(node.styles).map(([property, value]) => {
          if (typeof value !== 'string') return [property, value]
          const replaced = replacements.reduce(
            (current, replacement) =>
              current.includes(replacement.previousValue)
                ? current.replaceAll(
                    replacement.previousValue,
                    replacement.nextValue,
                  )
                : current,
            value,
          )
          return [property, replaced]
        }),
      )
      return [nodeId, { ...node, styles }]
    }),
  )

  return {
    ...document,
    tokens: { ...document.tokens, ...tokenPatch },
    nodes,
  }
}

function freshState() {
  const document = createInitialDocument()
  return {
    document,
    selectedNodeId: 'headline',
    editorDraft: compileDocument(document),
    codeError: null,
    history: [] as HistorySnapshot[],
    future: [] as HistorySnapshot[],
    mutations: [
      makeMutation('Hydrate shared graph', 'system', 1.8),
      makeMutation('Reconcile canvas tree', 'system', 3.4),
    ],
    branches: initialBranches.map((branch) => ({ ...branch })),
    commits: initialCommits.map((commit) => ({ ...commit })),
    activeBranchId: 'aurora-remix',
  }
}

export const useStudioStore = create<StudioState>((set, get) => {
  const commitDocument = (
    nextDocument: StudioDocument,
    label: string,
    source: ChangeSource,
    durationMs: number,
  ) => {
    const current = get()
    set({
      document: nextDocument,
      selectedNodeId: nextDocument.nodes[current.selectedNodeId]
        ? current.selectedNodeId
        : nextDocument.rootId,
      editorDraft: compileDocument(nextDocument),
      codeError: null,
      history: [
        ...current.history.slice(-(MAX_HISTORY - 1)),
        { document: current.document, label, source },
      ],
      future: [],
      mutations: [
        ...current.mutations.slice(-49),
        makeMutation(label, source, durationMs),
      ],
    })
  }

  return {
    ...freshState(),
    selectNode: (nodeId) => {
      if (get().document.nodes[nodeId]) set({ selectedNodeId: nodeId })
    },
    updateNode: (nodeId, patch, label = 'Update canvas node') => {
      const startedAt = performance.now()
      const current = get().document
      if (!current.nodes[nodeId]) return
      const next = applyDocumentPatch(current, {
        nodes: { [nodeId]: patch },
      })
      commitDocument(next, label, 'design', performance.now() - startedAt)
    },
    updateToken: (token, value) => {
      const startedAt = performance.now()
      const next = replaceTokenReferences(get().document, { [token]: value })
      commitDocument(
        { ...next, updatedAt: Date.now() },
        `Change ${token} token`,
        'design',
        performance.now() - startedAt,
      )
    },
    applyPatch: (patch, label, source = 'agent') => {
      const startedAt = performance.now()
      const current = get().document
      const tokenReconciled = replaceTokenReferences(
        current,
        patch.tokens ?? {},
      )
      const next = applyDocumentPatch(tokenReconciled, {
        ...patch,
        tokens: undefined,
      })
      commitDocument(next, label, source, performance.now() - startedAt)
    },
    setEditorDraft: (editorDraft) => set({ editorDraft, codeError: null }),
    applyEditorDraft: () => {
      const current = get()
      const result = parseDocumentCode(current.editorDraft, current.document)
      if (!result.document) {
        const error = result.error ?? 'Unable to apply code.'
        set({ codeError: error })
        return { ok: false, error }
      }
      commitDocument(result.document, 'Apply code changes', 'code', result.parseMs)
      return { ok: true }
    },
    formatEditorDraft: () => {
      set({ editorDraft: compileDocument(get().document), codeError: null })
    },
    undo: () => {
      const current = get()
      const previous = current.history.at(-1)
      if (!previous) return
      set({
        document: previous.document,
        editorDraft: compileDocument(previous.document),
        codeError: null,
        history: current.history.slice(0, -1),
        future: [
          ...current.future,
          {
            document: current.document,
            label: previous.label,
            source: previous.source,
          },
        ],
        mutations: [
          ...current.mutations.slice(-49),
          makeMutation(`Undo · ${previous.label}`, 'history', 0.4),
        ],
      })
    },
    redo: () => {
      const current = get()
      const next = current.future.at(-1)
      if (!next) return
      set({
        document: next.document,
        editorDraft: compileDocument(next.document),
        codeError: null,
        history: [
          ...current.history,
          {
            document: current.document,
            label: next.label,
            source: next.source,
          },
        ],
        future: current.future.slice(0, -1),
        mutations: [
          ...current.mutations.slice(-49),
          makeMutation(`Redo · ${next.label}`, 'history', 0.4),
        ],
      })
    },
    reset: () => set(freshState()),
    createRemix: () => {
      const current = get()
      const index = current.branches.length
      const id = `remix-${index}`
      const branch: BranchRecord = {
        id,
        name: `remix-${String(index).padStart(2, '0')}`,
        parentId: current.activeBranchId,
        author: 'you',
        createdAt: Date.now(),
        baseCode: compileDocument(current.document),
        color: index % 2 === 0 ? '#70d6ff' : '#ff8fab',
      }
      set({
        branches: [...current.branches, branch],
        activeBranchId: id,
        mutations: [
          ...current.mutations.slice(-49),
          makeMutation('Create local remix branch', 'system', 2.1),
        ],
      })
    },
    createCheckpoint: (message) => {
      const current = get()
      const commit: VersionCommit = {
        id: recordId().slice(0, 7),
        branchId: current.activeBranchId,
        message,
        author: 'you',
        at: Date.now(),
        code: compileDocument(current.document),
      }
      set({ commits: [...current.commits, commit] })
    },
  }
})

export function activeBranch(state: StudioState): BranchRecord {
  return (
    state.branches.find((branch) => branch.id === state.activeBranchId) ??
    state.branches[0]
  )
}
